import { useMemo, useCallback, useRef, useState } from "react";
import { useAccountant } from "./AccountantContext";
import FilterBar from "./FilterBar";
import EditableCell from "./EditableCell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MyobMappingDialog from "./MyobMappingDialog";
import {buildMyobMappingLabels, mappingDisplay} from "./myobMappingUtils";
import {
    getPropertyPricing,
    resetPropertyMyobOverride,
    setPropertyMyobOverride,
    updatePropertyPricing,
} from "../../service/PricingService";

const EDITABLE_PRICE_COLS = [
    { key: 'total_override', label: 'Total Price' },
    { key: 'labour_price', label: 'Labour Price' },
    { key: 'labour_override', label: 'Team Price' },
    { key: 'linens_price', label: 'Linens Price' },
    { key: 'consumable_price', label: 'Consumable Price' },
];

const headCellSx = { fontWeight: 500, whiteSpace: 'nowrap', fontSize: 13, fontStyle: 'italic' };
const bodyCellSx = { fontSize: 13, py: 0.5, whiteSpace: 'nowrap' };
const truncatedCellSx = { ...bodyCellSx, maxWidth: 200, overflow: 'hidden', textOverflow: 'clip' };
const agentCellSx = { ...bodyCellSx, maxWidth: 120, overflow: 'hidden', textOverflow: 'clip' };
const priceHeadSx = { ...headCellSx, width: 100, minWidth: 100 };
const priceCellSx = { ...bodyCellSx, width: 100, minWidth: 100 };
const paginationSx = { '& .MuiTablePagination-toolbar': { alignItems: 'baseline' } };

async function responseError(response, fallback) {
    if (response.ok) return null;
    const body = await response.json().catch(() => null);
    return new Error(body?.message || fallback);
}

function sourceLabel(source) {
    if (source === 'PROPERTY_OVERRIDE') return 'Property Override';
    if (source === 'AGENCY_DEFAULT') return 'Agency Default';
    return 'Unmapped';
}

export default function PropertyPricingTab() {
    const [state, dispatch] = useAccountant();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [editingMapping, setEditingMapping] = useState(null);
    const [savingMapping, setSavingMapping] = useState(false);
    const [exporting, setExporting] = useState(false);
    const exportInProgress = useRef(false);

    const filteredRows = useMemo(() => {
        return state.propertyPrice
            .filter(it => it.full_address?.toLowerCase().includes(state.searchAddress.toLowerCase()))
            .filter(it => it.agent_name ? it.agent_name.toLowerCase().includes(state.searchAgent.toLowerCase()) : state.searchAgent === '');
    }, [state.propertyPrice, state.searchAddress, state.searchAgent]);
    const mappingLabels = useMemo(
        () => buildMyobMappingLabels(state.myobProfiles, state.businessesAndAccounts),
        [state.myobProfiles, state.businessesAndAccounts]
    );

    const handleExport = useCallback(async () => {
        if (state.loading || savingMapping || exportInProgress.current || filteredRows.length === 0) return;

        exportInProgress.current = true;
        setExporting(true);
        try {
            const exportData = filteredRows.map(row => ({
                Address: row.full_address ?? '',
                'Agent Name': row.agent_name ?? '',
                'MYOB Mapping': mappingDisplay(row, mappingLabels),
                'MYOB Mapping Source': sourceLabel(row.myob_mapping_source),
                'Next Job Date': row.next_job_date ?? '',
                ...Object.fromEntries(EDITABLE_PRICE_COLS.map(col => [col.label, row[col.key] ?? ''])),
            }));
            const XLSX = await import('xlsx');
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            worksheet['!cols'] = [60, 25, 30, 24, 16, ...EDITABLE_PRICE_COLS.map(() => 18)]
                .map(wch => ({ wch }));
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Property Pricing');
            XLSX.writeFile(workbook, 'property_pricing.xlsx');
        } catch (error) {
            dispatch({
                type: 'SET_NOTIFICATION',
                payload: { message: 'Failed to export property pricing. Please try again.', isSuccess: false },
            });
        } finally {
            exportInProgress.current = false;
            setExporting(false);
        }
    }, [dispatch, filteredRows, mappingLabels, savingMapping, state.loading]);

    const refreshPropertyPrices = useCallback(() => {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_NOTIFICATION', payload: null });
        return getPropertyPricing(state.filterStartDate, state.filterEndDate, state.filterRegion)
            .then(res => { if (!res.ok) throw new Error('Failed to load property pricing'); return res.json(); })
            .then(data => {
                dispatch({ type: 'SET_PROPERTY_PRICING', payload: data });
                dispatch({ type: 'SET_LOADING', payload: false });
            })
            .catch(err => {
                dispatch({ type: 'SET_LOADING', payload: false });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            });
    }, [dispatch, state.filterStartDate, state.filterEndDate, state.filterRegion]);

    const handleUpdateValue = useCallback((propertyId, cell, oldValue, value) => {
        const updates = { [cell]: value };
        dispatch({ type: 'UPDATE_PROPERTY_CELL', payload: { propertyId, cell, value } });
        dispatch({ type: 'SET_LOADING', payload: true });
        updatePropertyPricing({ property_id: propertyId, ...updates })
            .then(async response => {
                const error = await responseError(response, 'Failed to update Property pricing');
                if (error) throw error;
            })
            .catch(err => {
                dispatch({ type: 'UPDATE_PROPERTY_CELL', payload: { propertyId, cell, value: oldValue } });
                dispatch({ type: 'SET_NOTIFICATION', payload: { message: err.message, isSuccess: false } });
            })
            .finally(() => dispatch({ type: 'SET_LOADING', payload: false }));
    }, [dispatch]);

    async function savePropertyOverride(mapping) {
        setSavingMapping(true);
        try {
            const response = await setPropertyMyobOverride(editingMapping.property_id, mapping);
            const error = await responseError(response, 'Failed to save Property Override');
            if (error) throw error;
            setEditingMapping(null);
            await refreshPropertyPrices();
        } catch (error) {
            dispatch({
                type: 'SET_NOTIFICATION',
                payload: {message: error.message, isSuccess: false},
            });
        } finally {
            setSavingMapping(false);
        }
    }

    async function resetPropertyOverride(row) {
        if (!window.confirm(`Reset ${row.full_address} to its Agency Default?`)) return;
        setSavingMapping(true);
        dispatch({type: 'SET_LOADING', payload: true});
        try {
            const response = await resetPropertyMyobOverride(row.property_id);
            const error = await responseError(response, 'Failed to reset Property Override');
            if (error) throw error;
            setEditingMapping(null);
            await refreshPropertyPrices();
        } catch (error) {
            dispatch({
                type: 'SET_NOTIFICATION',
                payload: {message: error.message, isSuccess: false},
            });
        } finally {
            setSavingMapping(false);
            dispatch({type: 'SET_LOADING', payload: false});
        }
    }

    const totalColSpan = 4 + EDITABLE_PRICE_COLS.length;

    return (
        <div>
            <FilterBar
                showRegionSelector
                showExportButton
                exportLabel={exporting ? 'Exporting...' : 'Export Excel'}
                exportDisabled={savingMapping || exporting || filteredRows.length === 0}
                onRefresh={refreshPropertyPrices}
                onExport={handleExport}
            />

            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headCellSx, minWidth: 180 }}>Address</TableCell>
                            <TableCell sx={headCellSx}>Agent Name</TableCell>
                            {EDITABLE_PRICE_COLS.map(col => (
                                <TableCell key={col.key} sx={priceHeadSx} title={col.label}>{col.label}</TableCell>
                            ))}
                            <TableCell sx={headCellSx}>Next Job Date</TableCell>
                            <TableCell sx={{ ...headCellSx, width: 180 }}>MYOB</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => (
                            <TableRow key={row.property_id} hover>
                                <TableCell sx={truncatedCellSx} title={row.full_address}>{row.full_address}</TableCell>
                                <TableCell sx={agentCellSx} title={row.agent_name}>{row.agent_name}</TableCell>
                                {EDITABLE_PRICE_COLS.map(col => (
                                    <TableCell key={col.key} sx={priceCellSx}>
                                        <EditableCell value={row[col.key]} type="number" onChange={(val) => handleUpdateValue(row.property_id, col.key, row[col.key], val)} />
                                    </TableCell>
                                ))}
                                <TableCell sx={bodyCellSx}>{row.next_job_date}</TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <span>{mappingDisplay(row, mappingLabels)}</span>
                                        <Button
                                            size="small"
                                            startIcon={<EditOutlinedIcon />}
                                            sx={{ textTransform: 'none' }}
                                            onClick={() => setEditingMapping(row)}
                                        >
                                            Edit
                                        </Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredRows.length === 0 && (
                            <TableRow><TableCell colSpan={totalColSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>No data to display</TableCell></TableRow>
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                count={filteredRows.length}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[20, 50, 100]}
                                labelDisplayedRows={({ from, to, count }) => `${count} total`}
                                sx={paginationSx}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
            <MyobMappingDialog
                open={Boolean(editingMapping)}
                title={`Property Override — ${editingMapping?.full_address || ''}`}
                profiles={state.myobProfiles}
                businessesAndAccounts={state.businessesAndAccounts}
                initialMapping={editingMapping}
                saving={savingMapping}
                onClose={() => setEditingMapping(null)}
                onSave={savePropertyOverride}
                onReset={editingMapping?.myob_mapping_source === 'PROPERTY_OVERRIDE'
                    ? () => resetPropertyOverride(editingMapping)
                    : undefined}
            />
        </div>
    );
}
